import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Select from 'react-select';
import MaterialTable, { MTableToolbar } from "material-table";
import { ThemeProvider } from "@mui/styles";
import { createTheme } from "@mui/material";
import "./candidate.css";
import { getPendingCandidateList, getPartnerList } from "../../lib/axiosUtils";
import ExcelExportComponent from "components/ExcelExportComponent";
import Spinner from 'components/Spinner';
import ShowToast from 'components/ShowToast';
import { TYPE_FAIL, INTERVAL, BK_SELECT_COLOR, SELECT_COLOR, COLOR_SELECT_COLOR, COLOR_PENDING_COLOR, CO_PENDING_COLOR } from "assets/constants/Constants";

const TJPScreeningQueue = () => {
    const history = useHistory();
    const mytheme = useMemo(() => createTheme({}), []);
    const [candidateData, setCandidateData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const ALL_OPTION = { value: "", label: "All" };
    const [engagementOptions] = useState([
        ALL_OPTION,
        { label: "CSR", value: "CSR" },
        { label: "RTD", value: "RTD" }
    ]);

    const [statusOptions] = useState([
        ALL_OPTION,
        { label: "Pending", value: "Pending" },
        { label: "Selected", value: "Selected" }
    ]);

    const [mobilizerOptions, setMobilizerOptions] = useState([ALL_OPTION]);

    const [filters, setFilters] = useState({
        status: "",
        mobilizer_id: "",
        engagement: ""
    });

    const fetchScreeningList = useCallback(async () => {
        try {
            setIsLoadingList(true);
            const params = new URLSearchParams();

            if (filters.mobilizer_id) {
                params.append("partner_id", filters.mobilizer_id);
            }

            if (filters.engagement) {
                params.append("engagement_type", filters.engagement);
            }

            if (filters.status) {
                params.append("screening_status", filters.status);
            }

            const [pendingCandidateList, mobilizerList] = await Promise.all([
                getPendingCandidateList({}, {}, `?${params.toString()}`),
                getPartnerList({}, {}),
            ]);

            setCandidateData(pendingCandidateList.data?.result || []);
            setMobilizerOptions([
                ALL_OPTION,
                ...(mobilizerList?.data?.result || []).map((mobilizer) => ({
                    label: mobilizer.training_partner,
                    value: mobilizer._id
                }))
            ]);

        } catch (error) {
            console.error("Screening List Fetch Error:", error);
            ShowToast('Unable to load screening list, please try again', TYPE_FAIL, INTERVAL, "", "");
        } finally {
            setIsLoading(false);
            setIsLoadingList(false);
        }
        // eslint-disable-next-line
    }, [filters]);

    useEffect(() => {
        fetchScreeningList();
    }, [fetchScreeningList, filters.status, filters.mobilizer_id, filters.engagement]);

    const reviewCandidate = (rowData) => {
        history.push("/screening-detail",
            {
                candidateId: rowData.candidateId,
                partnerId: rowData.partnerId,
                engagementType: rowData.engagementType,
                fullName: rowData.fullName
            });
    };

    const columns = [
        { title: "Sr. No", field: "srNo", cellStyle: { cellWidth: "2%", fontWeight: 'bolder', color: '#000000', fontSize: 14, textAlign: 'left' }, headerStyle: { textAlign: 'left' } },
        { title: "Candidate", field: "fullName", cellStyle: { width: "30%", fontWeight: "bolder", color: "#000000", fontSize: 14 } },
        { title: "Engagement Type", field: "engagementType", cellStyle: { width: "5%", color: "#808080", fontSize: 14 } },
        { title: "Training Partner", field: "mobilizer", cellStyle: { width: "40%", color: "#808080", fontSize: 14 } },
        {
            title: "Status",
            field: "status",
            cellStyle: {
                width: "10%",
                color: "#808080",
                fontSize: 14
            },
            render: (rowData) => (
                <span
                    style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "4px",
                        backgroundColor:
                            rowData.status === "Selected"
                                ? BK_SELECT_COLOR
                                : rowData.status === "Pending"
                                    ? SELECT_COLOR
                                    : "transparent",
                        color:
                            rowData.status === "Selected"
                                ? COLOR_SELECT_COLOR
                                : rowData.status === "Pending"
                                    ? COLOR_PENDING_COLOR
                                    : CO_PENDING_COLOR,
                        fontWeight: 600
                    }}
                >
                    {rowData.status}
                </span>
            )
        },
        { title: "Screener", field: "screener", cellStyle: { width: "30%", color: "#808080", fontSize: 14 } },
        { title: "Project", field: "assignedProject", cellStyle: { width: "40%", color: "#808080", fontSize: 14 } },
        { title: "Uploaded", field: "uploaded", cellStyle: { width: "10%", color: "#808080", fontSize: 14 }, sorting: false },
        {
            title: "",
            field: "review",
            sorting: false,
            render: (rowData) => (
                <button className="button-secondary width-auto screening-review-button" onClick={() => reviewCandidate(rowData)}>
                    Review
                </button>
            )
        }
    ];

    const handleFilterChange = (selectedOption, fieldName) => {
        setFilters((prevData) => ({
            ...prevData,
            [fieldName]: selectedOption ? selectedOption.value : ""
        }));
    };

    return (
        <>
            {isLoading ? (<Spinner />) : (
                <div className="main-content">
                    <div className="list-wrapper screening-page">
                        <div className="back">
                            <ul className="breadcrumb1">
                                <li>Screening queue</li>
                            </ul>
                        </div>
                        <div className="screening-filters">
                            <div className="input-label-group col-md-2">
                                <label className="form-label">Engagement</label>
                                <Select
                                    className="screening-select"
                                    classNamePrefix="screening-select"
                                    placeholder="All"
                                    options={engagementOptions}
                                    value={
                                        engagementOptions.find(
                                            option => option.value === filters.engagement
                                        ) || ALL_OPTION
                                    }
                                    onChange={(selectedOption) =>
                                        handleFilterChange(selectedOption, "engagement")
                                    }
                                />
                            </div>
                            <div className="input-label-group col-md-2">
                                <label className="form-label">Training Partner</label>
                                <Select
                                    className="screening-select"
                                    classNamePrefix="screening-select"
                                    placeholder="All"
                                    options={mobilizerOptions}
                                    value={
                                        mobilizerOptions.find(
                                            option => option.value === filters.mobilizer_id
                                        ) || ALL_OPTION
                                    }
                                    onChange={(selectedOption) =>
                                        handleFilterChange(selectedOption, "mobilizer_id")
                                    }
                                />
                            </div>
                            <div className="input-label-group col-md-2">
                                <label className="form-label">Status</label>
                                <Select
                                    className="screening-select"
                                    classNamePrefix="screening-select"
                                    placeholder="All"
                                    options={statusOptions}
                                    value={
                                        statusOptions.find(
                                            option => option.value === filters.status
                                        ) || ALL_OPTION
                                    }
                                    onChange={(selectedOption) =>
                                        handleFilterChange(selectedOption, "status")
                                    }
                                />
                            </div>
                        </div>
                        <div className="mt-4 border list-table-div" style={{ marginBottom: 50 }}>
                            {
                                isLoadingList ? (
                                    <div className="loading-height-screening-list">
                                        <Spinner />
                                    </div>
                                ) : (
                                    <ThemeProvider theme={mytheme}>
                                        <MaterialTable
                                            columns={columns}
                                            data={candidateData.map((item, index) => ({
                                                srNo: index + 1,
                                                fullName: item.first_name + " " + item.last_name,
                                                candidateId: item._id,
                                                mobilizer: item.training_partner,
                                                partnerId: item.partner_id,
                                                programId: item.program_id,
                                                status: item.screening_status,
                                                uploaded: new Date(item.created_date_time).toLocaleDateString('en-IN', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                }),
                                                screeningStatus: item.screening_status,
                                                engagementType: item.engagement_type,
                                                assignedProject: item.program_name,
                                                screener: item.screener || ""
                                            }))}
                                            // onRowClick={(_, rowData) => reviewCandidate(rowData.candidateId)}
                                            options={{
                                                actionsColumnIndex: -1,
                                                headerStyle: { color: "#999998", fontWeight: "bolder", fontSize: 15 },
                                                sorting: true,
                                                pageSize: 10,
                                                pageSizeOptions: [5, 10, 15, 20],
                                                search: true,
                                                columnsButton: true,
                                                exportButton: true,
                                                exportFileName: "Screening Candidate List",
                                            }}
                                            components={{
                                                Toolbar: (props) => (
                                                    <div>
                                                        <MTableToolbar {...props} />
                                                        {
                                                            candidateData.length > 0 && (
                                                                <ExcelExportComponent {...props} />
                                                            )
                                                        }
                                                    </div>
                                                ),
                                            }}
                                        />
                                    </ThemeProvider>
                                )
                            }
                        </div>
                    </div>
                </div>

            )}
        </>

    );
};

export default TJPScreeningQueue;